import { useEffect, useState } from 'react';

import { faEdit, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label } from '@repo/videa-components/src/index';

import type { ZuubOnboardingBeginResponse } from '@/@types/zuub/contracts';

import { CopyButton } from './components/CopyButton';

interface VideaUserInformationFormProps {
  onboardingData?: ZuubOnboardingBeginResponse;
  localPassword?: string;
  isSaving?: boolean;
  onSave?: (password: string) => void;
}

export const VideaUserInformationForm = ({
  onboardingData,
  localPassword,
  isSaving = false,
  onSave,
}: VideaUserInformationFormProps) => {
  const { t } = useLingui();

  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCopySuccess, setIsCopySuccess] = useState<string | null>(null);
  const [passwordValue, setPasswordValue] = useState(
    localPassword || onboardingData?.password || '',
  );

  // Format phone number for US/CA numbers as (XXX) XXX-XXXX
  // TODO: This should probably be a shared utility file
  const formatPhoneNumber = (phoneNumber?: string) => {
    if (!phoneNumber) return '';

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX for 10 digits
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }

    // Format as +1 (XXX) XXX-XXXX for 11 digits starting with 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
        4,
        7,
      )}-${cleaned.slice(7)}`;
    }

    // Return original if it doesn't match expected patterns
    return phoneNumber;
  };

  // Update password value when localPassword changes
  useEffect(() => {
    if (localPassword) {
      setPasswordValue(localPassword);
    }
  }, [localPassword]);

  const handleCopy = async (value: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopySuccess(fieldId);
      // Reset the success state after 2 seconds
      setTimeout(() => setIsCopySuccess(null), 2000);
    } catch (err) {
      console.error(`Failed to copy ${fieldId}:`, err);
    }
  };

  return (
    <>
      <div className="vai-flex vai-items-center vai-justify-between vai-mb-4 vai-h-10">
        <h3 className=" vai-font-semibold vai-mr-2">Videa User Information</h3>
        <div className="vai-flex vai-items-center vai-h-full vai-gap-4">
          {isEditing ? (
            <div className="vai-flex vai-gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (onSave && passwordValue) {
                    onSave(passwordValue);
                  }
                  setIsEditing(false);
                }}
                disabled={isSaving || !passwordValue}
              >
                {isSaving ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
              </Button>
            </div>
          ) : (
            <button
              className="vai-p-1 hover:vai-bg-gray-100 vai-rounded vai-h-8 vai-w-8 vai-flex vai-items-center vai-justify-center"
              onClick={() => setIsEditing(true)}
            >
              <FontAwesomeIcon
                icon={faEdit}
                className="vai-w-4 vai-h-4 vai-text-muted-foreground"
              />
            </button>
          )}
        </div>
      </div>

      <div className="vai-space-y-4">
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <div className="vai-group">
            <Label htmlFor="firstName">
              <Trans>First Name</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="firstName"
                type="text"
                value="Insurance"
                readOnly
                disabled
                className="vai-pr-10"
              />
              <CopyButton
                value="Insurance"
                fieldId="firstName"
                isCopied={isCopySuccess === 'firstName'}
                onCopy={handleCopy}
                copyLabel={t`Copy first name`}
              />
            </div>
          </div>
          <div className="vai-group">
            <Label htmlFor="lastName">
              <Trans>Last Name</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="lastName"
                type="text"
                value="Admin"
                readOnly
                disabled
                className="vai-pr-10"
              />
              <CopyButton
                value="Admin"
                fieldId="lastName"
                isCopied={isCopySuccess === 'lastName'}
                onCopy={handleCopy}
                copyLabel={t`Copy last name`}
              />
            </div>
          </div>
        </div>

        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <div className="vai-group">
            <Label htmlFor="dateOfBirth">
              <Trans>Date of Birth</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="dateOfBirth"
                type="date"
                // Hardcoded. Same for all accounts.
                value="1980-01-01"
                readOnly
                disabled
                className="vai-pr-10"
              />
              <CopyButton
                value="1980-01-01"
                fieldId="dateOfBirth"
                isCopied={isCopySuccess === 'dateOfBirth'}
                onCopy={handleCopy}
                copyLabel={t`Copy date of birth`}
              />
            </div>
          </div>
          <div className="vai-group">
            <Label htmlFor="phone">
              <Trans>Phone</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="phone"
                type="tel"
                value={formatPhoneNumber(onboardingData?.phoneNumber)}
                disabled
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value={onboardingData?.phoneNumber || ''}
                fieldId="phone"
                isCopied={isCopySuccess === 'phone'}
                onCopy={handleCopy}
                copyLabel={t`Copy phone`}
              />
            </div>
          </div>
        </div>

        <div className="vai-group">
          <Label htmlFor="username">
            <Trans>Username</Trans>
          </Label>
          <div className="vai-relative">
            <Input
              id="username"
              type="text"
              value={onboardingData?.username}
              readOnly
              disabled
              className="vai-pr-10"
            />
            <CopyButton
              value={onboardingData?.username || ''}
              fieldId="username"
              isCopied={isCopySuccess === 'username'}
              onCopy={handleCopy}
              copyLabel={t`Copy username`}
            />
          </div>
        </div>

        <div className="vai-group">
          <Label htmlFor="email">
            <Trans>Email Address</Trans>
          </Label>
          <div className="vai-relative">
            <Input
              id="email"
              type="email"
              value={onboardingData?.email}
              readOnly
              disabled
              className="vai-pr-10"
            />
            <CopyButton
              value={onboardingData?.email || ''}
              fieldId="email"
              isCopied={isCopySuccess === 'email'}
              onCopy={handleCopy}
              copyLabel={t`Copy email`}
            />
          </div>
        </div>

        <div className="vai-group">
          <Label htmlFor="password">
            <Trans>Password</Trans>
          </Label>
          <div className="vai-relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={passwordValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPasswordValue(e.target.value)
              }
              placeholder={t`Enter password`}
              readOnly={!isEditing}
              disabled={!isEditing}
              className="vai-pr-20"
            />
            <div className="vai-absolute vai-right-2 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-flex vai-gap-1">
              <CopyButton
                value={passwordValue}
                fieldId="password"
                isCopied={isCopySuccess === 'password'}
                onCopy={handleCopy}
                copyLabel={t`Copy password`}
                isAbsolutePositioned={false}
              />
              <button
                type="button"
                className="vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon
                  icon={showPassword ? faEyeSlash : faEye}
                  className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
